import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000'

// Cache-Control: no-store en cada request — sin esto, el navegador puede
// cachear por heurística una respuesta GET vieja (ej. si en algún momento un
// endpoint nuevo devolvió por error el index.html del SPA fallback, con
// headers Last-Modified/ETag como cualquier archivo estático) y seguir
// sirviéndola desde caché para siempre sin volver a pedirle nada al backend,
// aunque el backend ya esté arreglado. Bug real encontrado en desarrollo con
// GET /conversations/{phone}/messages.
export const api = axios.create({ baseURL: API_BASE_URL, headers: { 'Cache-Control': 'no-store' } })

// El access token vive solo en memoria (variable de módulo), nunca en
// localStorage — ver AuthContext.tsx para la justificación completa.
let authToken: string | null = null

export function setAuthToken(token: string | null): void {
  authToken = token
}

// La Sociedad activa también viaja en memoria; AuthContext es quien la
// persiste en localStorage y la restaura al montar.
let activeSociedadId: string | null = null

export function setActiveSociedadId(id: string | null): void {
  activeSociedadId = id
}

api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`
  }
  // Un caller puede fijar X-Sociedad-Id explícito en la request (ver
  // createMember) para apuntar a una Sociedad distinta de la activa — el
  // interceptor no lo pisa en ese caso, solo aplica el default global.
  // Usa .has()/.set() (API de AxiosHeaders) en vez de acceso por corchetes:
  // el nombre del header puede haber quedado normalizado con otra
  // capitalización, y el chequeo por corchetes no lo detecta — eso hacía
  // que este default siempre pisara el override explícito.
  if (activeSociedadId && !config.headers.has('X-Sociedad-Id')) {
    config.headers.set('X-Sociedad-Id', activeSociedadId)
  }
  return config
})

// Para llamadas que necesitan apuntar a una Sociedad puntual sin importar
// cuál esté activa globalmente (ej. el Owner gestionando Operadores de
// varias Sociedades desde una vista consolidada) — sin esto, el default del
// interceptor de arriba pisaría el destino real de la llamada.
function sociedadHeaders(sociedadId?: string): { headers: { 'X-Sociedad-Id': string } } | undefined {
  return sociedadId ? { headers: { 'X-Sociedad-Id': sociedadId } } : undefined
}

// Callback que AuthContext registra para reaccionar a un 401 (token vencido y
// sin refresh válido) reseteando el estado de auth y volviendo a mostrar Login.
export let onUnauthorized: () => void = () => {}
export function setOnUnauthorized(callback: () => void): void {
  onUnauthorized = callback
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      onUnauthorized()
    }
    return Promise.reject(error)
  },
)

export interface HealthResponse {
  status: string
  provider: string
  mock_billing: boolean
  mock_whatsapp: boolean
}

export interface ConversationSummary {
  phone: string
  client_name: string
  last_message: string
  last_role: 'user' | 'assistant' | 'agent' | string
  last_timestamp: string
  message_count: number
  paused: boolean
  handoff_requested: boolean
  handoff_reason: string
  handoff_requested_at: string
}

export interface ConversationDetail extends ConversationSummary {
  history: { role: 'system' | 'user' | 'assistant' | 'agent'; content: string }[]
  input_tokens_total: number
  output_tokens_total: number
  estimated_cost_usd: number
}

export interface StatsResponse {
  total_messages: number
  total_responded: number
  total_errors: number
}

export async function fetchHealth(): Promise<HealthResponse> {
  const { data } = await api.get<HealthResponse>('/health')
  return data
}

export async function fetchConversations(): Promise<ConversationSummary[]> {
  const { data } = await api.get<ConversationSummary[]>('/conversations')
  return data
}

export async function fetchConversationDetail(phone: string): Promise<ConversationDetail> {
  const { data } = await api.get<ConversationDetail>(`/conversations/${encodeURIComponent(phone)}`)
  return data
}

// ── Historial completo paginado (scroll infinito) ───────────────────────────

export interface MessageOut {
  id: string
  role: string
  content: string
  created_at: string
}

export interface MessageCursor {
  seq: number
}

export interface MessagePage {
  items: MessageOut[] // orden ascendente (viejo → nuevo) dentro de la página
  has_more: boolean
  next_cursor: MessageCursor | null
}

export async function fetchConversationMessages(
  phone: string,
  cursor?: MessageCursor,
  limit = 30,
): Promise<MessagePage> {
  const params: Record<string, string> = {
    limit: String(limit),
    // Cache-busting: sin esto, el navegador puede quedarse sirviendo para
    // siempre (caché heurística por Last-Modified) una respuesta vieja
    // cacheada bajo esta misma URL — pasó de verdad en desarrollo, antes de
    // que este endpoint existiera, el SPA fallback devolvió su index.html
    // con headers cacheables y el navegador nunca volvió a pedirle nada al
    // backend para esa conversación puntual. Un header Cache-Control en la
    // request no alcanza: el navegador decide si usa la caché antes de
    // mandar la petición.
    _: String(Date.now()),
  }
  if (cursor) {
    params.before_seq = String(cursor.seq)
  }
  const { data } = await api.get<MessagePage>(`/conversations/${encodeURIComponent(phone)}/messages`, { params })
  return data
}

export async function fetchStats(): Promise<StatsResponse> {
  const { data } = await api.get<StatsResponse>('/conversations/stats')
  return data
}

export async function pauseConversation(phone: string): Promise<void> {
  await api.post(`/conversations/${encodeURIComponent(phone)}/pause`)
}

export async function resumeConversation(phone: string): Promise<void> {
  await api.post(`/conversations/${encodeURIComponent(phone)}/resume`)
}

export async function sendAgentMessage(phone: string, text: string): Promise<void> {
  await api.post(`/conversations/${encodeURIComponent(phone)}/messages`, { text })
}

export function conversationsStreamUrl(accessToken: string, sociedadId: string): string {
  // EventSource nativo no puede mandar headers custom, así que el token y la
  // Sociedad activa viajan como query params — el backend los acepta ahí
  // solo para este endpoint (mismo mecanismo que X-Sociedad-Id).
  const params = new URLSearchParams({ token: accessToken, sociedad_id: sociedadId })
  return `${API_BASE_URL}/conversations/stream?${params.toString()}`
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
}

export type SociedadRank = 'owner' | 'administrador' | 'operador'

export interface SociedadMembership {
  id: string
  nombre: string
  rank: SociedadRank
  empresa_id: string
}

export interface MeResponse {
  user_id: string
  email: string
  nombre: string
  is_admin: boolean
  created_at: string
  sociedades: SociedadMembership[]
}

export async function login(email: string, password: string): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>('/auth/login', { email, password })
  return data
}

export interface RegisterPayload {
  email: string
  password: string
  nombre: string
  empresa_nombre: string
  sociedad_nombre: string
}

export async function register(payload: RegisterPayload): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>('/auth/register', payload)
  return data
}

export async function refreshToken(refresh_token: string): Promise<TokenResponse> {
  // Llamada directa con axios (no `api`) para no reintentar bajo el mismo
  // interceptor de 401 que dispara onUnauthorized si el refresh mismo falla.
  const { data } = await axios.post<TokenResponse>(`${API_BASE_URL}/auth/refresh`, { refresh_token })
  return data
}

export async function logout(refresh_token: string): Promise<void> {
  await api.post('/auth/logout', { refresh_token }).catch(() => undefined)
}

export async function fetchMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>('/auth/me')
  return data
}

export async function requestPasswordReset(email: string): Promise<void> {
  await api.post('/auth/password-reset/request', { email })
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  await api.post('/auth/password-reset/confirm', { token, new_password: newPassword })
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword })
}

// ── Números de WhatsApp (channel accounts) ──────────────────────────────────

export interface ChannelAccount {
  id: string
  sociedad_id: string
  display_name: string | null
  phone_number: string
  meta_phone_number_id: string
  meta_waba_id: string
  is_active: boolean
  token_expires_at: string | null
  created_at: string
}

export interface ChannelAccountCreatePayload {
  display_name?: string
  phone_number: string
  meta_phone_number_id: string
  meta_waba_id: string
  access_token: string
}

export async function fetchChannelAccounts(sociedadId?: string): Promise<ChannelAccount[]> {
  const { data } = await api.get<ChannelAccount[]>('/channel-accounts', sociedadHeaders(sociedadId))
  return data
}

export async function createChannelAccount(payload: ChannelAccountCreatePayload): Promise<ChannelAccount> {
  const { data } = await api.post<ChannelAccount>('/channel-accounts', payload)
  return data
}

export async function updateChannelAccount(
  id: string,
  payload: { display_name?: string; is_active?: boolean; access_token?: string },
): Promise<ChannelAccount> {
  const { data } = await api.patch<ChannelAccount>(`/channel-accounts/${id}`, payload)
  return data
}

// ── Onboarding self-service de WhatsApp (Meta Embedded Signup) ─────────────
// El cliente conecta su propio WhatsApp Business desde el frontend (Facebook
// Login for Business) sin ver ni pegar ningún token — ver
// frontend/src/components/EmbeddedSignupButton.tsx y
// app/services/whatsapp/embedded_signup.py. Requiere que la plataforma esté
// aprobada como Tech Provider ante Meta (ver CLAUDE.md); hasta entonces, el
// formulario manual de arriba sigue siendo el camino real.

export interface EmbeddedSignupPayload {
  code: string
  waba_id: string
  phone_number_id: string
}

export async function exchangeEmbeddedSignup(payload: EmbeddedSignupPayload): Promise<ChannelAccount> {
  const { data } = await api.post<ChannelAccount>('/channel-accounts/embedded-signup/exchange', payload)
  return data
}

export async function reauthEmbeddedSignup(
  channelAccountId: string,
  payload: EmbeddedSignupPayload,
): Promise<ChannelAccount> {
  const { data } = await api.post<ChannelAccount>(
    `/channel-accounts/${channelAccountId}/embedded-signup/reauth`,
    payload,
  )
  return data
}

// ── Base de conocimiento (FAQ) ───────────────────────────────────────────────

export interface KnowledgeBaseButton {
  id: string
  title: string
}

export interface KnowledgeBaseEntry {
  id: string
  sociedad_id: string
  faq_key: string
  triggers: string[]
  response: string
  buttons: KnowledgeBaseButton[] | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface KnowledgeBaseEntryPayload {
  faq_key: string
  triggers: string[]
  response: string
  buttons?: KnowledgeBaseButton[] | null
  is_active?: boolean
}

export async function fetchKnowledgeBaseEntries(): Promise<KnowledgeBaseEntry[]> {
  const { data } = await api.get<KnowledgeBaseEntry[]>('/knowledge-base')
  return data
}

export async function createKnowledgeBaseEntry(payload: KnowledgeBaseEntryPayload): Promise<KnowledgeBaseEntry> {
  const { data } = await api.post<KnowledgeBaseEntry>('/knowledge-base', payload)
  return data
}

export async function updateKnowledgeBaseEntry(
  id: string,
  payload: KnowledgeBaseEntryPayload,
): Promise<KnowledgeBaseEntry> {
  const { data } = await api.put<KnowledgeBaseEntry>(`/knowledge-base/${id}`, payload)
  return data
}

export async function deleteKnowledgeBaseEntry(id: string): Promise<void> {
  await api.delete(`/knowledge-base/${id}`)
}

// ── Miembros de una Sociedad ─────────────────────────────────────────────────

// El owner ya no es un rango de Sociedad (Fase 2.7) — es 1 por Empresa
// (ver transferEmpresaOwnership) y nunca aparece como valor asignable acá.
export type AssignableRank = 'administrador' | 'operador'

export interface Member {
  user_id: string
  nombre: string
  email: string
  rank: SociedadRank
  created_at: string
}

export interface MemberCreatePayload {
  nombre: string
  email: string
  password: string
  rank: AssignableRank
}

export async function fetchMembers(sociedadId: string): Promise<Member[]> {
  const { data } = await api.get<Member[]>(`/sociedades/${sociedadId}/members`, sociedadHeaders(sociedadId))
  return data
}

export async function createMember(sociedadId: string, payload: MemberCreatePayload): Promise<Member> {
  // Header explícito: permite crear el miembro en una Sociedad distinta de
  // la activa (ver selector de Sociedad en el diálogo de MembersPage).
  const { data } = await api.post<Member>(`/sociedades/${sociedadId}/members`, payload, sociedadHeaders(sociedadId))
  return data
}

export async function updateMember(sociedadId: string, userId: string, rank: AssignableRank): Promise<Member> {
  const { data } = await api.patch<Member>(
    `/sociedades/${sociedadId}/members/${userId}`,
    { rank },
    sociedadHeaders(sociedadId),
  )
  return data
}

export async function removeMember(sociedadId: string, userId: string): Promise<void> {
  await api.delete(`/sociedades/${sociedadId}/members/${userId}`, sociedadHeaders(sociedadId))
}

// ── Contactos (números de cliente) ──────────────────────────────────────────

export interface Contact {
  id: string
  phone: string
  display_name: string
  created_at: string
}

export async function fetchContacts(sociedadId?: string): Promise<Contact[]> {
  const { data } = await api.get<Contact[]>('/contacts', sociedadHeaders(sociedadId))
  return data
}

// ── Asignación de números a un Operador (Fase 2.7) ──────────────────────────

export async function fetchOperadorChannelAccounts(sociedadId: string, userId: string): Promise<string[]> {
  const { data } = await api.get<{ channel_account_ids: string[] }>(
    `/sociedades/${sociedadId}/operadores/${userId}/channel-accounts`,
    sociedadHeaders(sociedadId),
  )
  return data.channel_account_ids
}

export async function setOperadorChannelAccounts(
  sociedadId: string,
  userId: string,
  channelAccountIds: string[],
): Promise<string[]> {
  const { data } = await api.put<{ channel_account_ids: string[] }>(
    `/sociedades/${sociedadId}/operadores/${userId}/channel-accounts`,
    { channel_account_ids: channelAccountIds },
    sociedadHeaders(sociedadId),
  )
  return data.channel_account_ids
}

// ── Portal del Propietario: Sociedades y Administradores de su Empresa ─────

export interface SociedadIdentityFields {
  nombre_comercial: string | null
  tipo_identificacion: string | null
  numero_identificacion: string | null
  correo_electronico: string | null
  telefono: string | null
  direccion: string | null
  codigo_actividad: string | null
}

export interface EmpresaSociedadBrief extends SociedadIdentityFields {
  id: string
  nombre: string
  is_active: boolean
  created_at: string
}

export interface SociedadCreatePayload extends Partial<SociedadIdentityFields> {
  nombre: string
}

export interface EmpresaOwnerView {
  id: string
  nombre: string
  email_contacto: string | null
  is_active: boolean
  created_at: string
  sociedades: EmpresaSociedadBrief[]
}

export interface Administrador {
  user_id: string
  nombre: string
  email: string
  is_active: boolean
  sociedad_ids: string[]
}

export interface AdministradorCreatePayload {
  nombre: string
  email: string
  password: string
  sociedad_ids: string[]
}

export interface Operador {
  user_id: string
  nombre: string
  email: string
  sociedad_id: string
  sociedad_nombre: string
}

export async function fetchOperadores(empresaId: string): Promise<Operador[]> {
  const { data } = await api.get<Operador[]>(`/empresas/${empresaId}/operadores`)
  return data
}

export interface EmpresaContact {
  id: string
  phone: string
  display_name: string
  sociedad_id: string
  sociedad_nombre: string
  last_inbound_at: string | null
  created_at: string
}

export async function fetchEmpresaContactos(empresaId: string): Promise<EmpresaContact[]> {
  const { data } = await api.get<EmpresaContact[]>(`/empresas/${empresaId}/contactos`)
  return data
}

export interface ContactCreatePayload {
  sociedad_id: string
  phone: string
  display_name: string
}

export interface ContactUpdatePayload {
  phone: string
  display_name: string
}

export async function createEmpresaContacto(empresaId: string, payload: ContactCreatePayload): Promise<EmpresaContact> {
  const { data } = await api.post<EmpresaContact>(`/empresas/${empresaId}/contactos`, payload)
  return data
}

export async function updateEmpresaContacto(
  empresaId: string,
  contactId: string,
  payload: ContactUpdatePayload,
): Promise<EmpresaContact> {
  const { data } = await api.patch<EmpresaContact>(`/empresas/${empresaId}/contactos/${contactId}`, payload)
  return data
}

export async function deleteEmpresaContacto(empresaId: string, contactId: string): Promise<void> {
  await api.delete(`/empresas/${empresaId}/contactos/${contactId}`)
}

export async function fetchEmpresa(empresaId: string): Promise<EmpresaOwnerView> {
  const { data } = await api.get<EmpresaOwnerView>(`/empresas/${empresaId}`)
  return data
}

export interface SociedadDashboardStats {
  sociedad_id: string
  sociedad_nombre: string
  conversations: number
  messages_received: number
  messages_responded: number
}

export interface DashboardDailyPoint {
  date: string
  messages_received: number
  messages_responded: number
}

export interface EmpresaDashboard {
  date_from: string
  date_to: string
  total_conversations: number
  total_messages_received: number
  total_messages_responded: number
  by_sociedad: SociedadDashboardStats[]
  daily: DashboardDailyPoint[]
}

export interface DashboardFilters {
  dateFrom?: string
  dateTo?: string
  sociedadId?: string
}

export async function fetchEmpresaDashboard(empresaId: string, filters: DashboardFilters = {}): Promise<EmpresaDashboard> {
  const params: Record<string, string> = {}
  if (filters.dateFrom) params.date_from = filters.dateFrom
  if (filters.dateTo) params.date_to = filters.dateTo
  if (filters.sociedadId) params.sociedad_id = filters.sociedadId
  const { data } = await api.get<EmpresaDashboard>(`/empresas/${empresaId}/dashboard`, { params })
  return data
}

export async function createEmpresaSociedad(
  empresaId: string,
  payload: SociedadCreatePayload,
): Promise<EmpresaSociedadBrief> {
  const { data } = await api.post<EmpresaSociedadBrief>(`/empresas/${empresaId}/sociedades`, payload)
  return data
}

export async function activateEmpresaSociedad(empresaId: string, sociedadId: string): Promise<EmpresaSociedadBrief> {
  const { data } = await api.post<EmpresaSociedadBrief>(`/empresas/${empresaId}/sociedades/${sociedadId}/activate`)
  return data
}

export async function deactivateEmpresaSociedad(empresaId: string, sociedadId: string): Promise<EmpresaSociedadBrief> {
  const { data } = await api.post<EmpresaSociedadBrief>(`/empresas/${empresaId}/sociedades/${sociedadId}/deactivate`)
  return data
}

export async function fetchAdministradores(empresaId: string): Promise<Administrador[]> {
  const { data } = await api.get<Administrador[]>(`/empresas/${empresaId}/administradores`)
  return data
}

export async function createAdministrador(empresaId: string, payload: AdministradorCreatePayload): Promise<Administrador> {
  const { data } = await api.post<Administrador>(`/empresas/${empresaId}/administradores`, payload)
  return data
}

export async function updateAdministradorSociedades(
  empresaId: string,
  userId: string,
  sociedadIds: string[],
): Promise<Administrador> {
  const { data } = await api.put<Administrador>(`/empresas/${empresaId}/administradores/${userId}/sociedades`, {
    sociedad_ids: sociedadIds,
  })
  return data
}

export async function removeAdministrador(empresaId: string, userId: string): Promise<void> {
  await api.delete(`/empresas/${empresaId}/administradores/${userId}`)
}

export async function activateAdministrador(empresaId: string, userId: string): Promise<Administrador> {
  const { data } = await api.post<Administrador>(`/empresas/${empresaId}/administradores/${userId}/activate`)
  return data
}

export async function deactivateAdministrador(empresaId: string, userId: string): Promise<Administrador> {
  const { data } = await api.post<Administrador>(`/empresas/${empresaId}/administradores/${userId}/deactivate`)
  return data
}

export async function transferEmpresaOwnership(empresaId: string, userId: string): Promise<{ owner_user_id: string }> {
  const { data } = await api.post<{ owner_user_id: string }>(`/empresas/${empresaId}/transfer-ownership`, {
    user_id: userId,
  })
  return data
}

export async function updateEmpresaSociedad(
  empresaId: string,
  sociedadId: string,
  payload: SociedadCreatePayload,
): Promise<EmpresaSociedadBrief> {
  const { data } = await api.patch<EmpresaSociedadBrief>(`/empresas/${empresaId}/sociedades/${sociedadId}`, payload)
  return data
}

export interface ChatbotContextResponse {
  chatbot_context: string | null
}

export async function fetchChatbotContext(sociedadId: string): Promise<ChatbotContextResponse> {
  const { data } = await api.get<ChatbotContextResponse>(`/sociedades/${sociedadId}/chatbot-context`)
  return data
}

export async function updateChatbotContext(sociedadId: string, chatbotContext: string): Promise<ChatbotContextResponse> {
  const { data } = await api.patch<ChatbotContextResponse>(`/sociedades/${sociedadId}/chatbot-context`, {
    chatbot_context: chatbotContext,
  })
  return data
}

// ── Panel de SuperAdmin ──────────────────────────────────────────────────────

export interface AdminOverview {
  total_empresas: number
  total_sociedades: number
  total_usuarios: number
  total_channel_accounts_activos: number
  empresas_nuevas_7d: number
  empresas_nuevas_30d: number
}

export interface AdminEmpresaSummary {
  id: string
  nombre: string
  created_at: string
  sociedades_count: number
  usuarios_count: number
}

export interface AdminSociedadBrief {
  id: string
  nombre: string
  created_at: string
}

export interface AdminMemberBrief {
  user_id: string
  nombre: string
  email: string
  sociedad_nombre: string
  rank: SociedadRank
}

export interface AdminEmpresaDetail {
  id: string
  nombre: string
  email_contacto: string | null
  is_active: boolean
  created_at: string
  sociedades: AdminSociedadBrief[]
  miembros: AdminMemberBrief[]
}

export interface AdminSociedadSummary {
  id: string
  nombre: string
  empresa_id: string
  empresa_nombre: string
  channel_accounts_activos: number
  created_at: string
}

export interface AdminSuperAdmin {
  id: string
  nombre: string
  email: string
}

export async function fetchAdminOverview(): Promise<AdminOverview> {
  const { data } = await api.get<AdminOverview>('/admin/overview')
  return data
}

export async function fetchAdminEmpresas(): Promise<AdminEmpresaSummary[]> {
  const { data } = await api.get<AdminEmpresaSummary[]>('/admin/empresas')
  return data
}

export async function fetchAdminEmpresaDetail(empresaId: string): Promise<AdminEmpresaDetail> {
  const { data } = await api.get<AdminEmpresaDetail>(`/admin/empresas/${empresaId}`)
  return data
}

export async function fetchAdminSociedades(): Promise<AdminSociedadSummary[]> {
  const { data } = await api.get<AdminSociedadSummary[]>('/admin/sociedades')
  return data
}

export async function fetchSuperAdmins(): Promise<AdminSuperAdmin[]> {
  const { data } = await api.get<AdminSuperAdmin[]>('/admin/super-admins')
  return data
}

export async function promoteSuperAdmin(email: string): Promise<AdminSuperAdmin> {
  const { data } = await api.post<AdminSuperAdmin>('/admin/super-admins', { email })
  return data
}

export async function sendSupportPasswordReset(email: string): Promise<void> {
  await api.post('/admin/support/password-reset', { email })
}

// ── CRUD de Empresas del SuperAdmin (Fase 2.7) ──────────────────────────────

export interface AdminEmpresa {
  id: string
  nombre: string
  email_contacto: string | null
  is_active: boolean
  owner_user_id: string
  created_at: string
}

export interface EmpresaCreatePayload {
  nombre: string
  email_contacto?: string
  owner_nombre: string
  owner_email: string
  owner_password: string
  sociedad_nombre: string
}

export async function createEmpresaAdmin(payload: EmpresaCreatePayload): Promise<AdminEmpresa> {
  const { data } = await api.post<AdminEmpresa>('/admin/empresas', payload)
  return data
}

export async function updateEmpresaAdmin(
  empresaId: string,
  payload: { nombre?: string; email_contacto?: string },
): Promise<AdminEmpresa> {
  const { data } = await api.patch<AdminEmpresa>(`/admin/empresas/${empresaId}`, payload)
  return data
}

export async function activateEmpresaAdmin(empresaId: string): Promise<AdminEmpresa> {
  const { data } = await api.post<AdminEmpresa>(`/admin/empresas/${empresaId}/activate`)
  return data
}

export async function deactivateEmpresaAdmin(empresaId: string): Promise<AdminEmpresa> {
  const { data } = await api.post<AdminEmpresa>(`/admin/empresas/${empresaId}/deactivate`)
  return data
}
